import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { groupShareSessionAPI } from '../lib/api';
import { readGroupSessionStart } from '../lib/geolocate';
import { toast } from 'sonner';

/**
 * Shared launcher for a Group Share session. Returns `{ start, busy }`.
 *
 * Used by both `<StartGroupShareButton>` (pill button) and the dashed
 * full-width tile rendered at the bottom of the Groups page (session
 * 124u). GPS lookup is best-effort with a 4s timeout so a denied
 * permission never blocks session creation.
 */
export default function useStartGroupShare() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    let location = '';
    let locationGeo = null;
    try {
      const place = await Promise.race([
        readGroupSessionStart(),
        new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), 4000)),
      ]);
      location = place?.label || '';
      locationGeo = place?.geo || null;
    } catch {
      location = '';
      locationGeo = null;
    }
    try {
      const started = new Date();
      const meetingDate = `${started.getFullYear()}-${String(started.getMonth() + 1).padStart(2, '0')}-${String(started.getDate()).padStart(2, '0')}`;
      const { data } = await groupShareSessionAPI.create({
        location,
        meeting_date: meetingDate,
        location_geo: locationGeo,
      });
      navigate(`/session/host/${data.share_token}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start Group Share');
    }
    setBusy(false);
  };

  return { start, busy };
}
